/**
 * CanvasRenderer
 * Draws shelf detection overlays on a <canvas> exactly like the reference image:
 *   - Each cluster gets a random solid-colour filled rect + same-colour stroke
 *   - Article ID printed as small white text inside the top of each cluster box
 *   - Per-product boxes drawn with a lighter dashed stroke of the same colour
 *   - ESL boxes drawn in cyan
 *   - highlightByKey(labelCode) re-draws the matching cluster with a bright
 *     green thick border (same behaviour as VROGRenderer.highlightByKey)
 *
 * Public API (mirrors the VROGRenderer subset used in the component):
 *   new CanvasRenderer(containerEl, imageUrl, shelfInfo, bgColor?)
 *   await .render()
 *   .highlightByKey(labelCode)
 *   .destroy()
 *
 * shelfInfo format: already snake_case normalised array-of-arrays
 * (same object produced by _normaliseShelfInfo in the component).
 */

// One distinct colour per cluster index — vivid, same palette visible in the image
// const PALETTE = [
//   'rgba(255,  80,  80, 0.55)', // red
//   'rgba(180,  80, 220, 0.55)', // purple
//   'rgba( 80, 160, 255, 0.55)', // blue
//   'rgba(255, 180,  40, 0.55)', // orange
//   'rgba( 60, 200, 120, 0.55)', // green
//   'rgba(255,  80, 180, 0.55)', // pink
//   'rgba( 80, 220, 220, 0.55)', // teal
//   'rgba(255, 220,  60, 0.55)', // yellow
//   'rgba(140, 100, 255, 0.55)', // violet
//   'rgba(255, 120,  60, 0.55)' // amber
// ]

const STROKE = [
  'rgba(255,  80,  80, 1)',
  'rgba(180,  80, 220, 1)',
  'rgba( 80, 160, 255, 1)',
  'rgba(255, 180,  40, 1)',
  'rgba( 60, 200, 120, 1)',
  'rgba(255,  80, 180, 1)',
  'rgba( 80, 220, 220, 1)',
  'rgba(255, 220,  60, 1)',
  'rgba(140, 100, 255, 1)',
  'rgba(255, 120,  60, 1)'
]

const HIGHLIGHT_STROKE = '#00ff44' // bright green — same look as the green box in image
const ESL_STROKE = '#00e5ff' // cyan for ESL labels

export default class CanvasRenderer {
  /**
   * @param {HTMLElement} container   - The div ref (e.g. this.$refs.canvasContainer)
   * @param {string}      imageUrl    - Shelf photo URL
   * @param {Array}       shelfInfo   - Normalised snake_case array-of-arrays from _normaliseShelfInfo
   */
  constructor (container, imageUrl, shelfInfo) {
    this._container = container
    this._imageUrl = imageUrl
    this._shelfInfo = shelfInfo
    this._clusters = [] // flat list, built in _flatten()
    this._scale = 1
    this._canvas = null
    this._ctx = null
    this._img = null
    this._highlighted = null // label_code string of highlighted cluster
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  async render () {
    this._flatten()
    await this._loadImage()
    this._buildCanvas()
    this._draw()
  }

  /** Same signature as VROGRenderer.highlightByKey */
  highlightByKey (labelCode) {
    this._highlighted = labelCode || null
    this._draw()
  }

  destroy () {
    if (this._canvas && this._canvas.parentNode) {
      this._canvas.parentNode.removeChild(this._canvas)
    }
    this._canvas = null
    this._ctx = null
  }

  // ─── Internals ─────────────────────────────────────────────────────────────

  _flatten () {
    let idx = 0
    this._clusters = (this._shelfInfo || []).flat().map(cell => ({
      ...cell,
      _idx: idx++
    }))
  }

  _loadImage () {
    return new Promise(resolve => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => { this._img = img; resolve() }
      img.onerror = () => { this._img = null; resolve() }
      img.src = this._imageUrl
    })
  }

  _buildCanvas () {
    // Remove any previous canvas
    this.destroy()

    const NW = this._img ? this._img.naturalWidth : 1280
    const NH = this._img ? this._img.naturalHeight : 960

    // Fit to container width
    const maxW = (this._container.clientWidth || 500)
    this._scale = maxW / NW

    const canvas = document.createElement('canvas')
    canvas.width = NW * this._scale
    canvas.height = NH * this._scale
    canvas.style.cssText = 'display:block;width:100%;height:auto;'

    this._container.appendChild(canvas)
    this._canvas = canvas
    this._ctx = canvas.getContext('2d')
  }

  _draw () {
    const ctx = this._ctx
    if (!ctx) return

    const s = v => v * this._scale

    ctx.clearRect(0, 0, this._canvas.width, this._canvas.height)

    // ── Background image ────────────────────────────────────────────────────
    if (this._img) {
      ctx.drawImage(this._img, 0, 0, this._canvas.width, this._canvas.height)
    } else {
      ctx.fillStyle = '#1a1a1a'
      ctx.fillRect(0, 0, this._canvas.width, this._canvas.height)
    }

    this._clusters.forEach(cluster => {
      const i = cluster._idx
      const fill = 'transparent'
      const stroke = STROKE[i % STROKE.length]
      // eslint-disable-next-line camelcase
      const labelCode = cluster.esl_info?.[0]?.label_code || ''
      const isHL = labelCode !== '' && labelCode === this._highlighted
      // eslint-disable-next-line camelcase
      const [x1, y1, x2, y2] = cluster.cluster_bbox || [0, 0, 0, 0]
      const cw = s(x2 - x1)
      const ch = s(y2 - y1)

      // ── Cluster filled rect ─────────────────────────────────────────
      ctx.save()
      ctx.fillStyle = fill
      ctx.fillRect(s(x1), s(y1), cw, ch)

      // Cluster border — green + thicker when highlighted, else random colour
      ctx.strokeStyle = isHL ? cluster.weakConnection > 0 ? 'red' : HIGHLIGHT_STROKE : stroke
      ctx.lineWidth = isHL ? 3.5 : 2
      ctx.setLineDash([])
      if (isHL) {
        ctx.shadowColor = HIGHLIGHT_STROKE
        ctx.shadowBlur = 8
      }
      ctx.strokeRect(s(x1), s(y1), cw, ch)
      ctx.restore()

      // ── Article ID label (top-left inside cluster, like the image) ──
      // eslint-disable-next-line camelcase
      const artId = cluster.prd_info?.[0]?.article_id ||
                   cluster.prdInfo?.[0]?.articleId ||
                   ''
      // eslint-disable-next-line camelcase
      const artName = cluster.prd_info?.[0]?.article_name ||
                   cluster.prdInfo?.[0]?.articleName ||
                   ''
      const label = artId || artName.trim()

      if (label) {
        const fs = Math.max(8, Math.round(s(10)))
        ctx.save()
        ctx.font = `bold ${fs}px monospace`
        ctx.fillStyle = '#ffffff'
        ctx.textBaseline = 'top'
        // Thin dark shadow so text is readable on any background
        ctx.shadowColor = 'rgba(0,0,0,0.9)'
        ctx.shadowBlur = 3
        ctx.shadowOffsetX = 1
        ctx.shadowOffsetY = 1
        ctx.fillText(label, s(x1) + 3, s(y1) + 3)
        ctx.restore()
      }

      // ── Per-product dashed boxes ────────────────────────────────────
      // prdInfo comes through via _normaliseShelfInfo passthrough
      const products = cluster.prdInfo || []
      products.forEach(prod => {
        const [px1, py1, px2, py2] = prod.productBBox || []
        if (px1 == null) return
        ctx.save()
        ctx.strokeStyle = 'transparent'
        ctx.lineWidth = 1.5
        ctx.setLineDash([5, 3])
        ctx.strokeRect(s(px1), s(py1), s(px2 - px1), s(py2 - py1))
        ctx.restore()
      });

      // ── ESL box ─────────────────────────────────────────────────────
      // eslint-disable-next-line camelcase
      (cluster.esl_info || []).forEach(esl => {
        // eslint-disable-next-line camelcase
        const [ex1, ey1, ex2, ey2] = esl.esl_bbox || []
        if (ex1 == null) return
        ctx.save()
        ctx.strokeStyle = ESL_STROKE
        ctx.lineWidth = 1.5
        ctx.setLineDash([])
        ctx.strokeRect(s(ex1), s(ey1), s(ex2 - ex1), s(ey2 - ey1))
        ctx.restore()
      })
    })
  }
}
